<template>
  <div class="loading-page">
    <div class="loading-container">
      <h1 class="loading-title">
        {{ currentLoadingMessage }}<span class="dots">{{ animatedDots }}</span>
      </h1>
      <p class="loading-subheading">Please wait while we process your request…</p>
      <div class="spinner"></div>
    </div>
    <footer class="footer">
      <p>© {{ new Date().getFullYear() }} Taxscan Edutech Private Limited. All rights reserved.</p>
    </footer>
  </div>
</template>

<script>
import api from '@/api';

function isTokenExpired(token) {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    return Date.now() >= exp * 1000;
  } catch (e) {
    return true;
  }
}

export default {
  name: 'LoadingPage',
  data() {
    return {
      currentLoadingMessage: '🤖 AI is thinking',
      animatedDots: '',
      dotsInterval: null,
      messageInterval: null,
      timeoutTimer: null,
      loadingMessages: [
        '🤖 AI is thinking',
        '🔍 Searching for insights',
        '📊 Analyzing data',
        '📚 Looking up references',
        '⚡ Generating the best response',
        '🤯 Processing complex information',
        '🔄 Summarizing findings'
      ]
    };
  },
  async mounted() {
    this.startLoadingAnimation();

    const token = localStorage.getItem('auth_token');
    if (!token || isTokenExpired(token)) {
      alert("Session expired. Please log in again.");
      this.$router.replace({ name: 'LoginPage' });
      return;
    }

    const question = localStorage.getItem('user_question');
    const context  = localStorage.getItem('user_context');

    if (!question) {
      alert("Your question was not found. Please ask again.");
      this.$router.replace({ name: 'AskAIPage' });
      return;
    }

    // Set timeout to detect long-running AI
    this.timeoutTimer = setTimeout(() => {
      const retry = confirm("⏳ This is taking unusually long. Retry?");
      if (retry) {
        this.mounted(); // Re-run the request
      } else {
        this.$router.replace({ name: 'AskAIPage' });
      }
    }, 90000); // 90 seconds

    try {
      const resp = await api.post('/summary', { question, context });
      const data = resp.data;

      let raw = data.message || '';
      const prefix = 'Retrieved information:';
      if (raw.toLowerCase().startsWith(prefix.toLowerCase())) {
        raw = raw.slice(prefix.length).trim();
      }

      localStorage.setItem('ai_response', raw);
      localStorage.setItem('ai_sources', JSON.stringify(data.sources || []));
      localStorage.setItem('total_cost', String(data.totalCost || 0));

      this.$router.replace({ name: 'Response' });

    } catch (err) {
      console.error('Error fetching summary:', err);
      if (err.response?.status === 401) {
        alert("Session expired. Please log in again.");
        this.$router.replace({ name: 'LoginPage' });
      } else {
        const retry = confirm("⚠️ Failed to retrieve a response. Retry?");
        if (retry) {
          this.mounted(); // Retry again
        } else {
          this.$router.replace({ name: 'AskAIPage' });
        }
      }
    } finally {
      this.stopLoadingAnimation();
      clearTimeout(this.timeoutTimer);
    }
  },
  beforeUnmount() {
    this.stopLoadingAnimation();
    clearTimeout(this.timeoutTimer);
  },
  methods: {
    startLoadingAnimation() {
      this.dotsInterval = setInterval(() => {
        this.animatedDots =
          this.animatedDots.length < 3 ? this.animatedDots + '.' : '';
      }, 500);
      this.messageInterval = setInterval(() => {
        const idx = Math.floor(Math.random() * this.loadingMessages.length);
        this.currentLoadingMessage = this.loadingMessages[idx];
      }, 2000);
    },
    stopLoadingAnimation() {
      clearInterval(this.dotsInterval);
      clearInterval(this.messageInterval);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

.loading-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: var(--space-lg);
  background: var(--color-bg-gradient, linear-gradient(135deg, #e2f3f5, #ffffff));
  font-family: var(--font-sans, 'Poppins', sans-serif);
}

.loading-container {
  text-align: center;
  max-width: var(--content-max-width, 600px);
  margin: 0 auto;
}

.loading-title {
  font-size: var(--font-size-xl, 1.75rem);
  color: var(--color-text, #1f2937);
  margin-bottom: var(--space-md);
}

.dots {
  font-weight: bold;
}

.loading-subheading {
  font-size: var(--font-size-base, 1rem);
  color: var(--color-text-muted, #6b7280);
  margin-bottom: var(--space-lg);
}

.spinner {
  width: 60px;
  height: 60px;
  border: 6px solid var(--color-muted, rgba(0,0,0,0.1));
  border-left-color: var(--color-accent, #3b82f6);
  border-radius: 50%;
  animation: spin 1s infinite linear;
  margin: 0 auto var(--space-lg);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.footer {
  margin-top: auto;
  padding-top: var(--space-md);
  font-size: var(--font-size-sm, 0.875rem);
  color: var(--color-text-muted, #6b7280);
}
</style>
